import { useQuery, UseQueryOptions, UseQueryResult, QueryKey, QueryFunction } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { sessionExpiredAtom, userInfoAtom, verifyComponentAtom, verifyStepAtom } from '@/state/global'
import { handleAuthError, isAuthFailure } from '@/utils/authErrorHandler'

export function useAuthQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError>

export function useAuthQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>
): UseQueryResult<TData, TError>

export function useAuthQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  arg1: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> | TQueryKey,
  arg2?: QueryFunction<TQueryFnData, TQueryKey>,
  arg3?: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>
): UseQueryResult<TData, TError> {
  const setSessionExpired = useSetAtom(sessionExpiredAtom)
  const openVerifyComponent = useSetAtom(verifyComponentAtom)
  const setVerifyStep = useSetAtom(verifyStepAtom)
  const user = useAtomValue(userInfoAtom)

  const baseOptions: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> =
    arg2 !== undefined
      ? { queryKey: arg1 as TQueryKey, queryFn: arg2, ...(arg3 ?? {}) }
      : (arg1 as UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>)

  const { onError: userOnError, retry: userRetry, ...rest } = baseOptions

  const merged: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> = {
    ...rest,
    onError: async (error: TError) => {
      const handled = await handleAuthError(error, {
        onError: userOnError ? (e: unknown) => userOnError(e as TError) : undefined,
        setSessionExpired,
        openVerifyComponent,
        setVerifyStep,
        user,
      })
      if (!handled) {
        userOnError?.(error)
      }
    },
    retry: (failureCount, error) => {
      if (isAuthFailure(error)) return false
      if (userRetry === false) return false
      if (typeof userRetry === 'number') return failureCount < userRetry
      if (typeof userRetry === 'function') return userRetry(failureCount, error as TError)
      return failureCount < 3
    },
  }

  return useQuery(merged)
}
